/* eslint-disable max-len */
import React, { FC, useEffect, useMemo, useState } from 'react'
import Helmet from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import {
  createSettingRole,
  delSettingRole,
  fetchSettingRole,
  fetchSettingRoleDetail,
  updateSettingRole,
} from '../../../../states/containers/setting/systemSlice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { URL } from '../../../constants/url'
import LoadingSquare from '../../../components/common/LoadingSquare'
import AddMemberGroupingModule from '../../../components/setting/AddMemberGroupingModule'
import { replaceAndStrip } from '../../../../utils/string'
import Modal from '../../../components/common/Modal'
import { useSnackbar } from '../../../components/common/snackbar/SnackbarProvider'
import AddTeamAndMemberModule from '../../../components/setting/AddTeamAndMemberModule'
import { RoleList, ResponseGetSettingRoleDetail, RolePermission, RoleMember, RoleTeam } from '../../../../states/types/setting'
import { useHistory } from 'react-router-dom'
import { setPath } from '../../../../utils/url'
import { diffNowToDate } from '../../../../utils/date'
import { compile } from '../../../../utils/search'

interface Team {
  id: number
  scope: string
  name: string
  include_nested_teams: boolean | null
}

interface Item {
  key: string
  title: string
  description: string
  url: string
}

type RoleStatusKeys = keyof RolePermission

const COMMON_REQUIRED_LIST = [
  {
    key: "member_setting",
    title: "ユーザー設定",
    description: "Qaseeを利用するユーザーを設定します。",
    url: URL.SETTING_MEMBER.path,
  },
  {
    key: "team_setting",
    title: "チーム設定",
    description: "ユーザーが所属するチームを設定します。",
    url: URL.SETTING_TEAM.path,
  },
  {
    key: "member_import",
    title: "ユーザーCSV一括インポート",
    description: "ユーザー情報をCSVで一括インポートします。",
    url: URL.SETTING_IMPORT_CSV.path,
  },
]

const COMMON_COMPANY_LIST = [
  {
    key: "ip_restriction_setting",
    title: "IPアドレス制限設定",
    description: "アクセスできるIPアドレスを制限して、セキュリティを強化できます。",
    url: URL.SETTING_IP_RESTRICTION.path,
  },
  {
    key: "office_ip_address_setting",
    title: "オフィスのIPアドレス設定",
    description: "アクセス元によるパフォーマンス差異を計測できます。",
    url: URL.SETTING_OFFICE_IP_ADDRESS.path,
  },
  {
    key: "language_setting",
    title: "言語設定（配信メール・通知）",
    description: "Qaseeからの配信されるメールや通知で表示される言語を設定します。",
    url: URL.SETTING_LANGUAGE.path,
  },
  {
    key: "role_setting",
    title: "権限ロール設定",
    description: "権限ロールを設定できます。",
    url: URL.SETTING_ROLE.path,
  },
  {
    key: "custom_alert_setting",
    title: "アラート設定",
    description: "各種アラートを設定できます。",
    url: URL.CUSTOM_ALERT.path,
  },
  {
    key: "attendance_integration_setting",
    title: "勤怠データ連携設定",
    description: "ユーザーの勤怠データ連携を設定できます。",
    url: URL.SETTING_RECODE_API_CONNECT.path,
  },
  {
    key: "analysis_report_view",
    title: "組織分析レポートの閲覧",
    description: "組織分析レポートの閲覧を設定できます。",
    url: "",
  },
  {
    key: "approval_group_setting",
    title: "承認グループ設定",
    description: "ユーザーの承認フローを設定します。",
    url: URL.SETTING_TASK_APPROVAL_SETTING.path,
  },
]

const COMMON_TASK_LIST = [
  {
    key: "task_project_setting",
    title: "プロジェクト管理権限",
    description: "全プロジェクトの作成・編集・削除ができます。",
    url: URL.SETTING_IP_RESTRICTION.path,
  },
  {
    key: "task_project_import",
    title: "プロジェクトインポート権限",
    description: "プロジェクトインポート機能を使用して、一括でプロジェクトの作成・編集・削除ができます。",
    url: URL.SETTING_OFFICE_IP_ADDRESS.path,
  },
  {
    key: "task_project_create",
    title: "プロジェクト作成権限",
    description: "プロジェクトの新規作成ができます。",
    url: URL.SETTING_LANGUAGE.path,
  },
  {
    key: "task_category_setting",
    title: "カテゴリー管理権限",
    description: "全カテゴリーの作成・編集・削除ができます。",
    url: URL.SETTING_ROLE.path,
  },
  {
    key: "task_project_cost_view",
    title: "プロジェクトコスト表示権限",
    description: "プロジェクトレポートのコストに関する項目を表示できます。",
    url: URL.SETTING_LANGUAGE.path,
  },
  {
    key: "task_label_master_setting",
    title: "業務設定権限",
    description: "業務の作成・編集・削除ができます。",
    url: URL.SETTING_EXTERNAL_API_KEY.path,
  },
  {
    key: "task_target_service_setting",
    title: "仕分け対象アプリ設定権限",
    description: "仕分け対象アプリの設定・編集ができます。",
    url: URL.CUSTOM_ALERT.path,
  },
  {
    key: "task_member_cost_setting",
    title: "ユーザーコスト設定権限",
    description: "ユーザーコストの作成・編集・削除ができます。",
    url: URL.SETTING_TASK_APPROVAL_SETTING.path,
  },
  {
    key: "task_attendance_export",
    title: "勤怠データエクスポート",
    description: "勤怠データをエクスポートできます。",
    url: "",
  },
]

const PERSONAL_OPERATION_LIST = [
  {
    key: "measurement_setting",
    title: "計測時間設定",
    description: "Qaseeで計測する時間を設定できます。",
    url: URL.SETTING_MEASUREMENT.path,
  },
  {
    key: "base_work_time_setting",
    title: "標準勤務時刻・休憩時刻設定",
    description: "勤務開始・終了時間、休憩開始・終了時間が設定できます。",
    url: URL.SETTING_BASE_WORK_TIME.path,
  },
  {
    key: "holiday_setting",
    title: "休日設定",
    description: "ユーザーごとに休日が設定できます。",
    url: URL.SETTING_HOLIDAY.path,
  },
  {
    key: "measure_restriction_setting",
    title: "計測停止の制限設定",
    description: "ユーザーによる計測停止の制限を設定できます。",
    url: URL.SETTING_MEASUREMENT_RESTRICTION.path,
  },
  {
    key: "web_browser_setting",
    title: "Webブラウザの詳細非表示設定",
    description: "URLとページタイトルを非表示にできます。",
    url: URL.SETTING_WEB_BROWSER.path,
  },
  {
    key: "create_online_meeting_task_setting",
    title: "オンラインミーティング時の作業データ設定",
    description: "オンラインミーティング時、操作等がない時でも作業データを自動作成するように設定します。",
    url: URL.SETTING_TASK_ONLINE_MEETING_SETTING.path,
  },
  {
    key: "threshold_time_setting",
    title: "業務仕分けボードに表示する作業データの最小時間",
    description: "業務仕分けボードに表示する作業データの最小時間を設定します。",
    url: URL.SETTING_TASK_THRESHOLD_TIME_SETTING.path,
  },
  {
    key: "create_task_by_url_setting",
    title: "業務仕分けボードの作業データをURLごとに作成する",
    description: "業務仕分けボードの作業データをURLごとに作成するようにします。",
    url: URL.SETTING_TASK_CREATE_TASK_BY_URL_SETTING.path,
  },
  {
    key: "exclude_task_generate_member_setting",
    title: "作業データの非自動生成設定",
    description: "作業データの非自動生成を設定します。",
    url: URL.SETTING_TASK_EXCLUDE_GENERATE_MEMBER_SETTING.path,
  },
  {
    key: "learning_scope_setting",
    title: "仕分け提案に使用する過去データの範囲設定",
    description: "作業データの仕分け提案に使用する過去データの範囲を設定します。",
    url: URL.SETTING_TASK_LEARNING_SCOPE_SETTING.path,
  },
]

const SCOPE_LABEL: Record<string, string> = {
  both: "マネージャー・メンバー",
  manager: "マネージャー",
  member: "メンバー",
}

const Index: FC = () => {
  const {
    login,
    commonPart,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const dispatch = useDispatch()
  const history = useHistory()
  const { enqueue, closeQueue } = useSnackbar()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const roleStatusListInit: RolePermission = {
    member_setting: false,
    team_setting: false,
    member_import: false,
    ip_restriction_setting: false,
    office_ip_address_setting: false,
    language_setting: false,
    role_setting: false,
    contract_setting: false,
    external_api_key_setting: false,
    custom_alert_setting: false,
    attendance_integration_setting: false,
    analysis_report_view: false,
    approval_group_setting: false,
    task_project_setting: false,
    task_project_import: false,
    task_project_create: false,
    task_category_setting: false,
    task_project_cost_view: false,
    task_label_master_setting: false,
    task_target_service_setting: false,
    task_member_cost_setting: false,
    task_attendance_export: false,
    task_project_member_setting: false,
    measurement_setting: false,
    base_work_time_setting: false,
    holiday_setting: false,
    measure_restriction_setting: false,
    web_browser_setting: false,
    threshold_time_setting: false,
    exclude_task_generate_member_setting: false,
    learning_scope_setting: false,
    create_task_by_url_setting: false,
    create_online_meeting_task_setting: false,
  }
  const [roleStatusList, setRoleStatusList] = useState<RolePermission>(roleStatusListInit)

  const [selectRole, setSelectRole] = useState<ResponseGetSettingRoleDetail>()
  const [memberList, setMemberList] = useState<RoleMember[]>([])
  const [teamList, setTeamList] = useState<RoleTeam[]>([])
  const [isUpdate, setIsUpdate] = useState<boolean>(false)
  const [isDetailLoading, setIsDetailLoading] = useState<boolean>(false)

  const [inputName, setInputName] = useState<string>("")
  const [nameComposition, setNameComposition] = useState<boolean>(false)
  const [nameError, setNameError] = useState<string>("")

  const [inputDescription, setInputDescription] = useState<string>("")
  const [descriptionComposition, setDescriptionComposition] = useState<boolean>(false)

  const [attachedMemberIds, setAttachedMemberIds] = useState<number[]>([])
  const [targetMemberIds, setTargetMemberIds] = useState<number[]>([])
  const [targetTeams, setTargetTeams] = useState<Team[]>([])
  const [attachedMemberError, setAttachedMemberError] = useState<string>("")

  const [roleList, setRoleList] = useState<RoleList[]>([])
  const [searchWord, setSearchWord] = useState<string>("")
  const [composition, setComposition] = useState<boolean>(false)
  const [displayRoleList, setDisplayRoleList] = useState<RoleList[]>([])

  const [isDisplayDeleteRoleModal, setIsDisplayDeleteRoleModal] = useState<boolean>(false)
  const [deleteRoleId, setDeleteRoleId] = useState<number>(0)

  const [confirmModalState, setConfirmModalState] = useState<{ display: boolean, callback: VoidFunction }>(
    { display: false, callback: () => { /**/ } },
  )

  const [confirmRoleModalState, setConfirmRoleModalState] = useState<{ display: boolean, check: boolean, callback: VoidFunction }>(
    { display: false, check: false, callback: () => { /**/ } },
  )

  const [openTargetTeamIds, setOpenTargetTeamIds] = useState<number[]>([])
  const [selectAllTarget, setSelectAllTarget] = useState<boolean>(true)

  const alreadyPaidOrDuringTrial = useMemo(() => (
    commonPart.payment_finished || diffNowToDate(String(commonPart.trial_end_date)) >= 0
  ), [commonPart])

  const checkCommonRequiredList = useMemo(() => {
    const tmp = COMMON_REQUIRED_LIST.map((v) => v.key)
    return tmp.every((v) => roleStatusList[v as RoleStatusKeys])
  }, [roleStatusList])

  const checkCommonCompanyList = useMemo(() => {
    const tmp = COMMON_COMPANY_LIST.map((v) => v.key)
    if (login.feature_config?.external_api) {
      tmp.push("external_api_key_setting")
    }
    if (login.feature_config?.change_contract) {
      tmp.push("contract_setting")
    }
    return tmp.every((v) => roleStatusList[v as RoleStatusKeys])
  }, [roleStatusList])

  const checkCommonTaskList = useMemo(() => {
    const tmp = COMMON_TASK_LIST.map((v) => v.key)
    if (login.feature_config?.enable_project_member_setting) {
      tmp.push("task_project_member_setting")
    }
    return tmp.every((v) => roleStatusList[v as RoleStatusKeys])
  }, [roleStatusList])

  const checkPersonalList = useMemo(() => {
    const tmp = PERSONAL_OPERATION_LIST.map((v) => v.key)
    return tmp.every((v) => roleStatusList[v as RoleStatusKeys])
  }, [roleStatusList])

  const inRole = useMemo(() => (
    selectRole?.attached_members.map((v) => v.id).includes(login.current_member_id || 0) || false
  ), [selectRole])

  const reset = () => {
    setSearchWord("")
    setDisplayRoleList(roleList)
  }

  const search = () => {
    if (!searchWord) {
      reset()
      return
    }
    const word = searchWord.split(/\s+/)
    const list = roleList?.filter((role) => {
      if (role === null) return false
      return (compile(word).test(role.name))
    }) as RoleList[]
    setDisplayRoleList(list)
  }

  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !composition) {
      event.currentTarget.blur()
    }
  }

  const getAllMemberList = (id: number | null): RoleMember[] => {
    const tmpTeams = teamList.filter((v) => v.parent_team_id === id)
    if (!tmpTeams.length) return []
    const tmpMembers = [...tmpTeams.map((v) => [...v.managers, ...v.members]).flat(), ...tmpTeams.map((v) => getAllMemberList(v.id).flat()).flat()]
    return Array.from(new Map(tmpMembers.map((v) => [v.id, v])).values())
  }

  const displayUpdateToast = (queueId: number) => {
    setTimeout(() => {
      setIsUpdate(false)
      closeQueue(queueId)
      enqueue({
        type: "success",
        message: "設定を更新しました",
      })
    }, 1500)
  }

  const openConfirmRoleModal = (callback: VoidFunction) => {
    setConfirmRoleModalState({
      display: true,
      check: false,
      callback: () => {
        callback()
        setConfirmRoleModalState((cur) => ({
          ...cur,
          display: false,
          callback: () => { /* */ },
        }))
      },
    })
  }

  const toggleTargetTeamId = (teamId: number) => {
    if (openTargetTeamIds.includes(teamId)) {
      setOpenTargetTeamIds((cur) => cur.filter((v) => v !== teamId))
    } else {
      setOpenTargetTeamIds((cur) => [...cur, teamId])
    }
  }

  const changeInputName = (name: string) => {
    setInputName(name)
    const formatName = replaceAndStrip(name)
    if (roleList.filter((v) => v.id !== selectRole?.id && v.name === formatName).length) {
      setNameError("同名のロールが存在します。異なるロール名を設定してください。")
      setIsUpdate(false)
      return
    }
    setNameError("")
    setIsUpdate(true)
  }

  const changeInputDescription = (description: string) => {
    setInputDescription(description)
    setIsUpdate(true)
  }

  const changeSelectTargetType = (allTarget: boolean) => {
    setSelectAllTarget(allTarget)
    setIsUpdate(true)
  }

  const selectManagedMemberIds = (memberIds: number[]) => {
    if (!roleList.find((v) => v.id === selectRole?.id)?.editable && !memberIds.length) {
      setAttachedMemberError("管理者ロールの権限対象ユーザーは1名以上必要です")
      return
    }
    const otherRoleManagedMemberIds = Array.from(
      new Set(roleList.filter((v) => v.id && v.id !== selectRole?.id).map((v) => v.attached_member_ids).flat()),
    )
    if (memberIds.some((v) => otherRoleManagedMemberIds.includes(v))) {
      setConfirmModalState({
        display: true,
        callback: () => {
          setAttachedMemberIds(memberIds)
          setIsUpdate(true)
          setConfirmModalState((cur) => ({ ...cur, display: false }))
          setSelectRole((cur) => (cur ? ({ ...cur, attached_members: memberList.filter((v) => memberIds.includes(v.id)) }) : undefined))
          setRoleList((cur) => cur.map((v) => (v.id !== selectRole?.id
            ? { ...v, attached_member_ids: v.attached_member_ids.filter((w) => !memberIds.includes(w)) }
            : v
          )))
        },
      })
    } else {
      setAttachedMemberIds(memberIds)
      setIsUpdate(true)
    }
    setAttachedMemberError("")
  }

  const ejectManagedMemberId = (memberId: number) => {
    if (!roleList.find((v) => v.id === selectRole?.id)?.editable && attachedMemberIds.length === 1 && attachedMemberIds[0] === memberId) {
      setAttachedMemberError("管理者ロールの権限対象ユーザーは1名以上必要です")
      return
    }
    setAttachedMemberIds((cur) => cur.filter((v) => v !== memberId))
    setSelectRole((cur) => (cur ? ({ ...cur, attached_members: cur.attached_members.filter((v) => v.id !== memberId) }) : undefined))
    setRoleList((cur) => cur.map((v) => (!v.id
      ? { ...v, attached_member_ids: [...v.attached_member_ids, memberId] }
      : v
    )))
    setIsUpdate(true)
    setAttachedMemberError("")
  }

  const selectTargetMemberIds = (memberIds: number[]) => {
    setTargetMemberIds(memberIds)
    setIsUpdate(true)
  }

  const ejectTargetMemberId = (memberId: number) => {
    setTargetMemberIds((cur) => cur.filter((v) => v !== memberId))
    setIsUpdate(true)
  }

  const selectTargetTeams = (teams: Team[]) => {
    setTargetTeams(teams)
    setIsUpdate(true)
  }

  const ejectTargetTeamId = (teamId: number) => {
    setTargetTeams((cur) => cur.filter((v) => v.id !== teamId))
    setIsUpdate(true)
  }

  const changeRoleStatus = (key: string) => {
    if (!roleList.find((v) => v.id === selectRole?.id)?.editable) return
    if (inRole && key === "role_setting") {
      openConfirmRoleModal(() => {
        setRoleStatusList((cur) => ({ ...cur, [key]: !cur[key as RoleStatusKeys] }))
        setIsUpdate(true)
      })
      return
    }
    setRoleStatusList((cur) => ({ ...cur, [key]: !cur[key as RoleStatusKeys] }))
    setIsUpdate(true)
  }

  const multiChangeRoleStatus = (type: string) => {
    if (!roleList.find((v) => v.id === selectRole?.id)?.editable) return
    let list: string[] = []
    switch (type) {
      case "common_required":
        list = COMMON_REQUIRED_LIST.map((v) => v.key)
        break
      case "common_company":
        list = COMMON_COMPANY_LIST.map((v) => v.key)
        if (login.feature_config?.external_api) {
          list.push("external_api_key_setting")
        }
        if (login.feature_config?.change_contract) {
          list.push("contract_setting")
        }
        break
      case "common_task":
        list = COMMON_TASK_LIST.map((v) => v.key)
        if (login.feature_config?.enable_project_member_setting) {
          list.push("task_project_member_setting")
        }
        break
      case "personal":
        list = PERSONAL_OPERATION_LIST.map((v) => v.key)
        break
      default:
        list = []
    }
    const tmpValue: {[key: string]: boolean} = {}
    if (list.every((v) => roleStatusList[v as RoleStatusKeys])) {
      list.forEach((key) => { tmpValue[key] = false })
    } else {
      list.forEach((key) => { tmpValue[key] = true })
    }
    if (inRole && type === "common_company" && !tmpValue.role_setting) {
      openConfirmRoleModal(() => {
        setRoleStatusList((cur) => ({
          ...cur,
          ...tmpValue,
        }))
        setIsUpdate(true)
      })
      return
    }
    setRoleStatusList((cur) => ({
      ...cur,
      ...tmpValue,
    }))
    setIsUpdate(true)
  }

  const buildCheckColor = (checked: boolean) => {
    if (!roleList.find((v) => v.id === selectRole?.id)?.editable) {
      return "#999999"
    }
    if (checked) {
      return "#008800"
    }
    return "#666666"
  }

  const buildItem = (item: Item) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        width: "calc(50% - 8px)",
        padding: "8px",
        cursor: "pointer",
      }}
      onClick={() => changeRoleStatus(item.key)}
      role="none"
    >
      <div
        style={{
          fontSize: "18px",
          lineHeight: "0.5em",
          backgroundColor: "#FFFFFF",
          color: buildCheckColor(roleStatusList[item.key as RoleStatusKeys]),
        }}
      >
        <FontAwesomeIcon
          icon={[
            roleList.find((v) => v.id === selectRole?.id)?.editable ? 'far' : 'fas',
            roleStatusList[item.key as RoleStatusKeys] ? 'check-square' : 'square',
          ]}
        />
      </div>
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          gap: "4px",
          width: "100%",
        }}
      >
        <div
          style={{
            fontSize: "16px",
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            color: "#666666",
          }}
        >
          {item.description}
        </div>
      </div>
    </div>
  )

  const clickRole = (id: number) => {
    setIsDetailLoading(true)
    dispatch(
      fetchSettingRoleDetail(
        { id: id.toString() },
        (resultDetail) => {
          setSelectRole(resultDetail)
          setRoleStatusList(resultDetail.permission)
          setIsDetailLoading(false)
          setInputName(resultDetail.name)
          setInputDescription(resultDetail.description || "")
          setAttachedMemberIds(resultDetail.attached_members.map((v) => v.id))
          setTargetMemberIds(resultDetail.target_members.map((v) => v.id))
          setTargetTeams(resultDetail.target_teams)
          setSelectAllTarget(!resultDetail.target_restriction)
          setNameError("")
          setAttachedMemberError("")
          setSelectRole(resultDetail)
          setIsLoading(false)
        },
      ),
    )
  }

  const createRole = () => {
    setIsDetailLoading(true)
    setIsUpdate(true)
    dispatch(
      createSettingRole(
        (result) => {
          const tmp = {
            id: result.id,
            name: result.name,
            description: "",
            attached_members: [],
            permission: roleStatusListInit,
            target_restriction: false,
            target_members: [],
            target_teams: [],
          }
          setSearchWord("")
          setRoleList((cur) => ([...cur, { id: tmp.id, name: tmp.name, editable: true, attached_member_ids: [] }]))
          setSelectRole(tmp)
          setRoleStatusList(roleStatusListInit)
          setIsUpdate(false)
          setInputName(result.name)
          setInputDescription("")
          setAttachedMemberIds([])
          setTargetMemberIds([])
          setTargetTeams([])
          setSelectAllTarget(true)
          setIsDetailLoading(false)
        },
      ),
    )
  }

  const openDeleteRoleModal = (roleId: number) => {
    setIsDisplayDeleteRoleModal(true)
    setDeleteRoleId(roleId)
  }

  const closeDeleteRoleModal = () => {
    setIsDisplayDeleteRoleModal(false)
    setDeleteRoleId(0)
  }

  const deleteRole = (roleId: number, isSelf = false) => {
    const deleteRoleManagedMemberIds = roleList.find((v) => v.id === roleId)?.attached_member_ids || []
    setRoleList((cur) => (cur.filter((v) => v.id !== roleId))
      .map((v) => (!v.id ? { ...v, attached_member_ids: [...v.attached_member_ids, ...deleteRoleManagedMemberIds] } : v)))
    setIsDetailLoading(true)
    const queueId = enqueue({
      type: "progress",
      message: "設定を更新しています…",
    })
    dispatch(
      delSettingRole(
        { id: roleId.toString() },
        () => {
          displayUpdateToast(queueId)
          closeDeleteRoleModal()
          if (isSelf) {
            history.push(setPath(URL.SETTING_PERSONAL.path, "ja"))
          } else {
            dispatch(
              fetchSettingRoleDetail(
                { id: roleList[0].id.toString() },
                (resultDetail) => {
                  setSelectRole(resultDetail)
                  setRoleStatusList(resultDetail.permission)
                  setIsUpdate(false)
                  setInputName(resultDetail.name)
                  setInputDescription(resultDetail.description || "")
                  setAttachedMemberIds(resultDetail.attached_members.map((v) => v.id))
                  setTargetMemberIds(resultDetail.target_members.map((v) => v.id))
                  setSelectAllTarget(!resultDetail.target_restriction)
                  setIsDetailLoading(false)
                },
              ),
            )
          }
        },
      ),
    )
  }

  const selectTarget = (data: {teams: Team[], members: { id: number, name: string }[]}) => {
    selectTargetMemberIds(data.members.map((v) => v.id))
    selectTargetTeams(data.teams)
  }

  useEffect(() => {
    if (!login.accessToken) return
    setIsLoading(true)
    setConfirmRoleModalState({ display: false, check: false, callback: () => { /**/ } })
    dispatch(
      fetchSettingRole(
        (result) => {
          setRoleList(result.roles)
          setMemberList(result.members)
          setTeamList(result.teams)
          setIsLoading(false)
          setIsDetailLoading(true)
          dispatch(
            fetchSettingRoleDetail(
              { id: result.roles[0].id.toString() },
              (resultDetail) => {
                clickRole(resultDetail.id)
                setSelectRole(resultDetail)
                setSelectAllTarget(!resultDetail.target_restriction)
                setIsDetailLoading(false)
              },
            ),
          )
        },
      ),
    )
  }, [login.accessToken])

  useEffect(() => {
    const formatName = replaceAndStrip(inputName)
    const timer = setTimeout(() => {
      if (isUpdate && !nameComposition && formatName) {
        const queueId = enqueue({
          type: "progress",
          message: "設定を更新しています…",
        })
        setRoleList((cur) => cur.map((v) => (v.id === selectRole?.id ? { ...v, name: formatName } : v)))
        dispatch(
          updateSettingRole(
            {
              id: selectRole?.id.toString() || "0",
              name: formatName,
            },
            () => {
              displayUpdateToast(queueId)
            },
          ),
        )
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [inputName, nameComposition])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isUpdate && !descriptionComposition) {
        const queueId = enqueue({
          type: "progress",
          message: "設定を更新しています…",
        })
        dispatch(
          updateSettingRole(
            {
              id: selectRole?.id.toString() || "0",
              description: inputDescription,
            },
            () => {
              displayUpdateToast(queueId)
            },
          ),
        )
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [inputDescription, descriptionComposition])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isUpdate) {
        const queueId = enqueue({
          type: "progress",
          message: "設定を更新しています…",
        })
        setRoleList((cur) => cur.map((v) => (v.id === selectRole?.id ? { ...v, attached_member_ids: attachedMemberIds } : v)))
        dispatch(
          updateSettingRole(
            {
              id: selectRole?.id.toString() || "0",
              attached_member_ids: attachedMemberIds,
            },
            () => {
              displayUpdateToast(queueId)
              if (confirmRoleModalState.check) {
                history.push(setPath(URL.SETTING_PERSONAL.path, "ja"))
              }
            },
          ),
        )
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [attachedMemberIds])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isUpdate) {
        const queueId = enqueue({
          type: "progress",
          message: "設定を更新しています…",
        })
        dispatch(
          updateSettingRole(
            {
              id: selectRole?.id.toString() || "0",
              target_restriction: !selectAllTarget,
            },
            () => {
              displayUpdateToast(queueId)
            },
          ),
        )
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [selectAllTarget])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isUpdate) {
        const queueId = enqueue({
          type: "progress",
          message: "設定を更新しています…",
        })
        dispatch(
          updateSettingRole(
            {
              id: selectRole?.id.toString() || "0",
              target_member_ids: targetMemberIds,
              target_teams: targetTeams,
            },
            () => {
              displayUpdateToast(queueId)
            },
          ),
        )
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [targetMemberIds, targetTeams])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isUpdate) {
        const queueId = enqueue({
          type: "progress",
          message: "設定を更新しています…",
        })
        const tmp = {
          member_setting: roleStatusList.member_setting,
          team_setting: roleStatusList.team_setting,
          member_import: roleStatusList.member_import,
          ip_restriction_setting: roleStatusList.ip_restriction_setting,
          office_ip_address_setting: roleStatusList.office_ip_address_setting,
          language_setting: roleStatusList.language_setting,
          role_setting: roleStatusList.role_setting,
          contract_setting: roleStatusList.contract_setting,
          external_api_key_setting: roleStatusList.external_api_key_setting,
          custom_alert_setting: roleStatusList.custom_alert_setting,
          attendance_integration_setting: roleStatusList.attendance_integration_setting,
          analysis_report_view: roleStatusList.analysis_report_view,
          approval_group_setting: roleStatusList.approval_group_setting,
          task_project_setting: roleStatusList.task_project_setting,
          task_project_import: roleStatusList.task_project_import,
          task_project_create: roleStatusList.task_project_create,
          task_category_setting: roleStatusList.task_category_setting,
          task_project_cost_view: roleStatusList.task_project_cost_view,
          task_label_master_setting: roleStatusList.task_label_master_setting,
          task_target_service_setting: roleStatusList.task_target_service_setting,
          task_member_cost_setting: roleStatusList.task_member_cost_setting,
          task_attendance_export: roleStatusList.task_attendance_export,
          task_project_member_setting: roleStatusList.task_project_member_setting,
          measurement_setting: roleStatusList.measurement_setting,
          base_work_time_setting: roleStatusList.base_work_time_setting,
          holiday_setting: roleStatusList.holiday_setting,
          measure_restriction_setting: roleStatusList.measure_restriction_setting,
          web_browser_setting: roleStatusList.web_browser_setting,
          threshold_time_setting: roleStatusList.threshold_time_setting,
          exclude_task_generate_member_setting: roleStatusList.exclude_task_generate_member_setting,
          learning_scope_setting: roleStatusList.learning_scope_setting,
          create_task_by_url_setting: roleStatusList.create_task_by_url_setting,
          create_online_meeting_task_setting: roleStatusList.create_online_meeting_task_setting,
        }
        dispatch(
          updateSettingRole(
            {
              id: selectRole?.id.toString() || "0",
              permission: tmp,
            },
            () => {
              displayUpdateToast(queueId)
              if (confirmRoleModalState.check) {
                history.push(setPath(URL.SETTING_PERSONAL.path, "ja"))
              }
            },
          ),
        )
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [roleStatusList])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!!roleList.length && !composition) {
        search()
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [searchWord, composition, roleList])

  useEffect(() => {
    if (!roleList.length) return
    search()
  }, [roleList])

  return (
    <section className="setting_page">
      <Helmet>
        <title>Qasee - 設定 - 権限ロール設定</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        権限ロール設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          権限ロール設定
        </h1>
      </section>
      <section className="page_contents content-area">
        <div
          className="content-block"
          style={{
            margin: 0,
            padding: 0,
          }}
        >
          <div className="block-detail">
            {
              isLoading ? (
                <>
                  <LoadingSquare>
                    <>
                      情報を取得しています…
                    </>
                  </LoadingSquare>
                </>
              ) : (
                <>
                  {
                    alreadyPaidOrDuringTrial ? (
                      <>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: "#EEEEEE",
                              width: "250px",
                              padding: "16px",
                              display: "flex",
                              flexFlow: "column",
                              gap: "8px",
                              height: "fit-content",
                            }}
                          >
                            <div
                              style={{
                                position: "sticky",
                                top: "60px",
                                zIndex: "1",
                              }}
                            >
                              <input
                                type="text"
                                placeholder="ロール名で検索"
                                style={{
                                  width: "100%",
                                  padding: "8px",
                                }}
                                onChange={(event) => setSearchWord(event.target.value)}
                                // onBlur={search}
                                onKeyDown={(event) => keyDown(event)}
                                onCompositionStart={() => setComposition(true)}
                                onCompositionEnd={() => setComposition(false)}
                                value={searchWord}
                              />
                              {
                                searchWord && (
                                  <button
                                    type="button"
                                    style={{
                                      position: "absolute",
                                      top: "50%",
                                      right: "3px",
                                      padding: "0",
                                      transform: "translate(-50%, -50%)",
                                      color: "#999999",
                                    }}
                                    onClick={reset}
                                  >
                                    <FontAwesomeIcon icon={['far', 'times-circle']} />
                                  </button>
                                )
                              }
                            </div>
                            <button
                              type="button"
                              style={{
                                padding: "12px 8px",
                                borderRadius: "4px",
                                textAlign: "center",
                                cursor: "pointer",
                                backgroundColor: "#425E96",
                                color: "#FFFFFF",
                                display: "flex",
                                gap: "4px",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                              onClick={createRole}
                              disabled={isUpdate}
                            >
                              <FontAwesomeIcon icon={['fas', 'plus']} />
                              新規ロール作成
                            </button>
                            {
                              displayRoleList.map((v) => (
                                <button
                                  key={`role_${v.id}_${v.name}`}
                                  type="button"
                                  style={{
                                    position: "relative",
                                    padding: "12px 32px 12px 8px",
                                    borderRadius: "4px",
                                    textAlign: "center",
                                    cursor: "pointer",
                                    backgroundColor: selectRole?.id === v.id ? "#C9DBFD" : "transparent",
                                    lineBreak: "anywhere",
                                  }}
                                  onClick={() => clickRole(v.id)}
                                  disabled={isUpdate}
                                >
                                  {v.name}
                                  {
                                    v.editable && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          openDeleteRoleModal(v.id)
                                          e.stopPropagation()
                                        }}
                                        style={{
                                          position: "absolute",
                                          top: "50%",
                                          right: 0,
                                          transform: "translateY(-50%)",
                                          color: "#a90000",
                                        }}
                                      >
                                        <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                                      </button>
                                    )
                                  }
                                </button>
                              ))
                            }
                            {
                              !!searchWord.length && !displayRoleList.length && (
                                <div>
                                  {`「${searchWord}」に該当するロールはありません`}
                                </div>
                              )
                            }
                          </div>
                          <div
                            style={{
                              position: "relative",
                              width: "calc(100% - 258px)",
                            }}
                          >
                            <div
                              key={`role_id_${selectRole?.id}`}
                              style={{
                                width: "100%",
                                padding: "16px 16px 64px",
                                display: "flex",
                                flexFlow: "column",
                                gap: "24px",
                                position: "sticky",
                                top: "60px",
                                overflowY: "scroll",
                                scrollbarWidth: "none",
                                maxHeight: "100vh",
                              }}
                            >
                              {
                                isDetailLoading ? (
                                  <>
                                    <LoadingSquare>
                                      <>
                                        情報を取得しています…
                                      </>
                                    </LoadingSquare>
                                  </>
                                ) : (
                                  <>
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "16px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: "50%",
                                          display: "flex",
                                          flexFlow: "column",
                                          gap: "4px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                          }}
                                        >
                                          ロール名
                                        </div>
                                        <div
                                          style={{
                                            position: "relative",
                                          }}
                                        >
                                          {
                                            roleList.find((v) => v.id === selectRole?.id)?.editable ? (
                                              <>
                                                <input
                                                  type="text"
                                                  style={{
                                                    width: "100%",
                                                    padding: "12px",
                                                    borderRadius: "4px",
                                                    border: `solid 1px ${nameError ? "#FF0000" : "#999999"}`,
                                                  }}
                                                  value={inputName}
                                                  onChange={(e) => changeInputName(e.target.value)}
                                                  onCompositionStart={() => setNameComposition(true)}
                                                  onCompositionEnd={() => setNameComposition(false)}
                                                  placeholder="ロール名を入力してください(必須)"
                                                />
                                                <div
                                                  style={{
                                                    position: "absolute",
                                                    top: "50%",
                                                    right: "16px",
                                                    transform: "translateY(-50%)",
                                                    color: "#CCCCCC",
                                                  }}
                                                >
                                                  <FontAwesomeIcon icon={['fas', 'pen']} />
                                                </div>
                                                {
                                                  nameError && (
                                                    <div
                                                      style={{
                                                        color: "#ff0000",
                                                        fontSize: "10px",
                                                        padding: "4px",
                                                      }}
                                                    >
                                                      {nameError}
                                                    </div>
                                                  )
                                                }
                                              </>
                                            ) : (
                                              <div
                                                style={{
                                                  width: "100%",
                                                  padding: "12px",
                                                  borderRadius: "4px",
                                                  backgroundColor: "#EEEEEE",
                                                  color: "#999999",
                                                }}
                                              >
                                                {selectRole?.name}
                                              </div>
                                            )
                                          }
                                        </div>
                                      </div>
                                      <div
                                        style={{
                                          width: "50%",
                                          display: "flex",
                                          flexFlow: "column",
                                          gap: "4px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                          }}
                                        >
                                          説明
                                        </div>
                                        <div
                                          style={{
                                            position: "relative",
                                          }}
                                        >
                                          {
                                            roleList.find((v) => v.id === selectRole?.id)?.editable ? (
                                              <>
                                                <input
                                                  type="text"
                                                  style={{
                                                    width: "100%",
                                                    padding: "12px",
                                                    borderRadius: "4px",
                                                  }}
                                                  value={inputDescription}
                                                  onChange={(e) => changeInputDescription(e.target.value)}
                                                  onCompositionStart={() => setDescriptionComposition(true)}
                                                  onCompositionEnd={() => setDescriptionComposition(false)}
                                                  placeholder="説明を入力してください(任意)"
                                                />
                                                <div
                                                  style={{
                                                    position: "absolute",
                                                    top: "50%",
                                                    right: "16px",
                                                    transform: "translateY(-50%)",
                                                    color: "#CCCCCC",
                                                  }}
                                                >
                                                  <FontAwesomeIcon icon={['fas', 'pen']} />
                                                </div>
                                              </>
                                            ) : (
                                              <div
                                                style={{
                                                  width: "100%",
                                                  padding: "12px",
                                                  borderRadius: "4px",
                                                  backgroundColor: "#EEEEEE",
                                                  color: "#999999",
                                                }}
                                              >
                                                {selectRole?.description}
                                              </div>
                                            )
                                          }
                                        </div>
                                      </div>
                                    </div>
                                    <div>
                                      <div
                                        style={{
                                          width: "100%",
                                          display: "flex",
                                          flexFlow: "column",
                                          gap: "4px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                          }}
                                        >
                                          権限対象ユーザー
                                        </div>
                                        <div
                                          style={{
                                            position: "relative",
                                            border: "solid 1px #CCCCCC",
                                            borderRadius: "4px",
                                            backgroundColor: "#FFFFFF",
                                            padding: "8px",
                                            display: "flex",
                                            flexFlow: "column",
                                            gap: "4px",
                                          }}
                                        >
                                          {
                                            !!attachedMemberIds.length && (
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  flexWrap: "wrap",
                                                  gap: "4px",
                                                  maxHeight: "200px",
                                                  overflowY: "scroll",
                                                  scrollbarWidth: "none",
                                                }}
                                              >
                                                {
                                                  attachedMemberIds.map((memberId) => (
                                                    <div
                                                      style={{
                                                        border: "solid 1px #CCCCCC",
                                                        borderRadius: "20px",
                                                        padding: "4px 8px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                        userSelect: "none",
                                                        backgroundColor: "#FFFFFF",
                                                      }}
                                                      key={`attached_member_id_${memberId}`}
                                                    >
                                                      <img
                                                        alt="profile"
                                                        src={
                                                          memberList.find((v) => v.id === memberId)?.profile_image
                                                          ?? `/static/images/profile/${(Number(memberId) || 0) % 12}.jpeg`
                                                        }
                                                        className="profile_icon"
                                                        style={{
                                                          borderRadius: "50%",
                                                          width: "14px",
                                                        }}
                                                      />
                                                      {memberList.find((v) => v.id === memberId)?.name}
                                                      {
                                                        (!!selectRole?.id) && (
                                                          <>
                                                            {
                                                              login.current_member_id === memberId ? (
                                                                <>
                                                                  <span
                                                                    style={{
                                                                      cursor: "pointer",
                                                                    }}
                                                                    onClick={() => openConfirmRoleModal(() => ejectManagedMemberId(memberId))}
                                                                    role="none"
                                                                  >
                                                                    <FontAwesomeIcon icon={['fas', 'times']} />
                                                                  </span>
                                                                </>
                                                              ) : (
                                                                <>
                                                                  <span
                                                                    style={{
                                                                      cursor: "pointer",
                                                                    }}
                                                                    onClick={() => ejectManagedMemberId(memberId)}
                                                                    role="none"
                                                                  >
                                                                    <FontAwesomeIcon icon={['fas', 'times']} />
                                                                  </span>
                                                                </>
                                                              )
                                                            }
                                                          </>
                                                        )
                                                      }
                                                    </div>
                                                  ))
                                                }
                                              </div>
                                            )
                                          }
                                          {
                                            !!selectRole?.id && (
                                              <div
                                                style={{
                                                  marginTop: "4px",
                                                }}
                                              >
                                                <AddMemberGroupingModule
                                                  memberIdList={attachedMemberIds || []}
                                                  callbackMembers={(val) => selectManagedMemberIds(val.map((v) => v.id))}
                                                  memberList={
                                                    memberList.map((v) => ({ id: v.id, name: v.name, profile_image: v.profile_image, active: v.active }))
                                                  }
                                                  teamList={teamList.map((v) => ({
                                                    ...v,
                                                    team_id: v.id,
                                                    count: 0,
                                                    created_at: "",
                                                    managers: v.managers.map((w) => ({ ...w, contract: "", place: "" })),
                                                    members: v.members.map((w) => ({ ...w, contract: "", place: "" })),
                                                  }))}
                                                />
                                              </div>
                                            )
                                          }
                                        </div>
                                        {
                                          !!attachedMemberError && (
                                            <div
                                              style={{
                                                padding: "8px 0",
                                                color: "#FF0000",
                                              }}
                                            >
                                              {attachedMemberError}
                                            </div>
                                          )
                                        }
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        position: "relative",
                                        display: "flex",
                                        flexFlow: "column",
                                        gap: "16px",
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "4px",
                                        padding: "24px 16px 16px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: "-7px",
                                          left: "12px",
                                          fontSize: "14px",
                                          fontWeight: "bold",
                                          backgroundColor: "#FFFFFF",
                                          padding: "0 4px",
                                        }}
                                      >
                                        各種設定
                                      </div>
                                      <div>
                                        <div
                                          style={{
                                            width: "100%",
                                            display: "flex",
                                            flexFlow: "column",
                                            gap: "4px",
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontSize: "14px",
                                              fontWeight: "bold",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "8px",
                                              cursor: "pointer",
                                            }}
                                            onClick={() => multiChangeRoleStatus("common_required")}
                                            role="none"
                                          >
                                            <div
                                              style={{
                                                fontSize: "18px",
                                                color: buildCheckColor(checkCommonRequiredList),
                                              }}
                                            >
                                              <FontAwesomeIcon
                                                icon={[
                                                  roleList.find((v) => v.id === selectRole?.id)?.editable ? 'far' : 'fas',
                                                  checkCommonRequiredList ? 'check-square' : 'square',
                                                ]}
                                              />
                                            </div>
                                            必須設定
                                          </div>
                                          <div
                                            style={{
                                              borderRadius: "4px",
                                              backgroundColor: "#F5F0F0",
                                              padding: "8px",
                                              display: "flex",
                                              flexFlow: "column",
                                              gap: "8px",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                flexWrap: "wrap",
                                              }}
                                            >
                                              {
                                                COMMON_REQUIRED_LIST.map((v) => (
                                                  <>
                                                    {buildItem(v)}
                                                  </>
                                                ))
                                              }
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div>
                                        <div
                                          style={{
                                            width: "100%",
                                            display: "flex",
                                            flexFlow: "column",
                                            gap: "4px",
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontSize: "14px",
                                              fontWeight: "bold",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "8px",
                                              cursor: "pointer",
                                            }}
                                            onClick={() => multiChangeRoleStatus("common_company")}
                                            role="none"
                                          >
                                            <div
                                              style={{
                                                fontSize: "18px",
                                                color: buildCheckColor(checkCommonCompanyList),
                                              }}
                                            >
                                              <FontAwesomeIcon
                                                icon={[
                                                  roleList.find((v) => v.id === selectRole?.id)?.editable ? 'far' : 'fas',
                                                  checkCommonCompanyList ? 'check-square' : 'square',
                                                ]}
                                              />
                                            </div>
                                            全社設定
                                          </div>
                                          <div
                                            style={{
                                              borderRadius: "4px",
                                              backgroundColor: "#F5F0F0",
                                              padding: "8px",
                                              display: "flex",
                                              flexFlow: "column",
                                              gap: "8px",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                flexWrap: "wrap",
                                              }}
                                            >
                                              {
                                                COMMON_COMPANY_LIST.map((v) => (
                                                  <>
                                                    {buildItem(v)}
                                                  </>
                                                ))
                                              }
                                              {
                                                login.feature_config?.external_api && (
                                                  <>
                                                    {buildItem({
                                                      key: "external_api_key_setting",
                                                      title: "APIキー設定",
                                                      description: "APIキーを設定できます。",
                                                      url: URL.SETTING_EXTERNAL_API_KEY.path,
                                                    })}
                                                  </>
                                                )
                                              }
                                              {
                                                login.feature_config?.change_contract && (
                                                  <>
                                                    {buildItem({
                                                      key: "contract_setting",
                                                      title: "契約変更設定",
                                                      description: "アカウント数の追加やオプションの追加を設定できます。",
                                                      url: URL.SETTING_CONTRACT.path,
                                                    })}
                                                  </>
                                                )
                                              }
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div>
                                        <div
                                          style={{
                                            width: "100%",
                                            display: "flex",
                                            flexFlow: "column",
                                            gap: "4px",
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontSize: "14px",
                                              fontWeight: "bold",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "8px",
                                              cursor: "pointer",
                                            }}
                                            onClick={() => multiChangeRoleStatus("personal")}
                                            role="none"
                                          >
                                            <div
                                              style={{
                                                fontSize: "18px",
                                                color: buildCheckColor(checkPersonalList),
                                              }}
                                            >
                                              <FontAwesomeIcon
                                                icon={[
                                                  roleList.find((v) => v.id === selectRole?.id)?.editable ? 'far' : 'fas',
                                                  checkPersonalList ? 'check-square' : 'square',
                                                ]}
                                              />
                                            </div>
                                            個別設定
                                          </div>
                                          <div
                                            style={{
                                              borderRadius: "4px",
                                              backgroundColor: "#ECF0F4",
                                              padding: "24px",
                                              display: "flex",
                                              flexFlow: "column",
                                              gap: "16px",
                                            }}
                                          >
                                            {
                                              roleList.find((v) => v.id === selectRole?.id)?.editable && (
                                                <div
                                                  style={{
                                                    width: "100%",
                                                    display: "flex",
                                                    flexFlow: "column",
                                                    gap: "4px",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      fontSize: "14px",
                                                      fontWeight: "bold",
                                                    }}
                                                  >
                                                    設定対象範囲
                                                  </div>
                                                  <div
                                                    style={{
                                                      position: "relative",
                                                      border: "solid 1px #CCCCCC",
                                                      borderRadius: "4px",
                                                      backgroundColor: "#FFFFFF",
                                                      padding: "8px",
                                                      display: "flex",
                                                      flexFlow: "column",
                                                      gap: "8px",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: "4px",
                                                          color: selectAllTarget ? "#008800" : "#666666",
                                                          cursor: "pointer",
                                                          userSelect: "none",
                                                        }}
                                                        onClick={() => changeSelectTargetType(true)}
                                                        role="none"
                                                      >
                                                        <FontAwesomeIcon
                                                          icon={[
                                                            selectAllTarget ? 'fas' : 'far',
                                                            selectAllTarget ? 'check-circle' : 'circle',
                                                          ]}
                                                        />
                                                        すべてのユーザー
                                                      </div>
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: "4px",
                                                          color: !selectAllTarget ? "#008800" : "#666666",
                                                          cursor: "pointer",
                                                          userSelect: "none",
                                                        }}
                                                        onClick={() => changeSelectTargetType(false)}
                                                        role="none"
                                                      >
                                                        <FontAwesomeIcon
                                                          icon={[
                                                            !selectAllTarget ? 'fas' : 'far',
                                                            !selectAllTarget ? 'check-circle' : 'circle',
                                                          ]}
                                                        />
                                                        チーム・ユーザーを指定
                                                      </div>
                                                    </div>
                                                    {
                                                      !selectAllTarget && (!!targetTeams.length || !!targetMemberIds.length) && (
                                                        <div
                                                          style={{
                                                            display: "flex",
                                                            flexFlow: "column",
                                                            gap: "8px",
                                                            maxHeight: "400px",
                                                            overflowY: "scroll",
                                                            scrollbarWidth: "none",
                                                            paddingBottom: "12px",
                                                          }}
                                                        >
                                                          {
                                                            targetTeams.map((team) => {
                                                              const tmpTeam = teamList.find((v) => v.id === team.id)
                                                              if (!tmpTeam) return (<></>)
                                                              return (
                                                                <div
                                                                  style={{
                                                                    position: "relative",
                                                                    border: openTargetTeamIds.includes(tmpTeam.id) ? "solid 1px #CCCCCC" : "none",
                                                                    borderRadius: "4px",
                                                                    padding: openTargetTeamIds.includes(tmpTeam.id) ? "20px 8px 8px" : "0",
                                                                    display: "flex",
                                                                    flexFlow: "column",
                                                                    gap: "8px",
                                                                    marginTop: "16px",
                                                                    backgroundColor: openTargetTeamIds.includes(tmpTeam.id) ? "#FFFFFF" : "transparent",
                                                                  }}
                                                                  key={`target_team_id_${tmpTeam.id}`}
                                                                >
                                                                  <button
                                                                    type="button"
                                                                    style={{
                                                                      position: "absolute",
                                                                      top: "0",
                                                                      left: "4px",
                                                                      fontSize: "12px",
                                                                      transform: "translateY(-50%)",
                                                                      padding: "2px 8px",
                                                                      backgroundColor: "#FFFFFF",
                                                                      border: "solid 1px #CCCCCC",
                                                                      borderRadius: "16px",
                                                                      display: "flex",
                                                                      alignItems: "center",
                                                                      gap: "8px",
                                                                    }}
                                                                    onClick={() => toggleTargetTeamId(tmpTeam.id)}
                                                                  >
                                                                    {/* eslint-disable-next-line max-len */}
                                                                    {`${tmpTeam.name}の${SCOPE_LABEL[team.scope]}${team.include_nested_teams ? " + 配下チーム" : ""}`}
                                                                    <FontAwesomeIcon
                                                                      icon={[
                                                                        'fas', openTargetTeamIds.includes(tmpTeam.id) ? 'angle-up' : 'angle-down',
                                                                      ]}
                                                                    />
                                                                  </button>
                                                                  {
                                                                    openTargetTeamIds.includes(tmpTeam.id) && (
                                                                      <button
                                                                        type="button"
                                                                        style={{
                                                                          position: "absolute",
                                                                          top: "0",
                                                                          right: "4px",
                                                                          fontSize: "10px",
                                                                          transform: "translateY(-50%)",
                                                                          padding: "2px 8px",
                                                                          backgroundColor: "#ffeeee",
                                                                          border: "solid 1px #FF0000",
                                                                          color: "#FF0000",
                                                                          borderRadius: "16px",
                                                                          display: "flex",
                                                                          alignItems: "center",
                                                                          gap: "8px",
                                                                        }}
                                                                        onClick={() => ejectTargetTeamId(tmpTeam.id)}
                                                                      >
                                                                        このチームを設定対象範囲から解除する
                                                                      </button>
                                                                    )
                                                                  }
                                                                  {
                                                                    openTargetTeamIds.includes(tmpTeam.id) && ["both", "manager"].includes(team.scope) && (
                                                                      <div
                                                                        style={{
                                                                          position: "relative",
                                                                          border: "solid 1px #CCCCCC",
                                                                          borderRadius: "4px",
                                                                          backgroundColor: "#fbf4ff",
                                                                          padding: "12px 8px 8px",
                                                                          display: "flex",
                                                                          flexWrap: "wrap",
                                                                          gap: "4px",
                                                                          marginTop: "4px",
                                                                        }}
                                                                      >
                                                                        <div
                                                                          style={{
                                                                            position: "absolute",
                                                                            top: "0",
                                                                            left: "4px",
                                                                            fontSize: "10px",
                                                                            transform: "translateY(-50%)",
                                                                            padding: "2px 8px",
                                                                            backgroundColor: "#FFFFFF",
                                                                            border: "solid 1px #CCCCCC",
                                                                            borderRadius: "14px",
                                                                          }}
                                                                        >
                                                                          マネージャー
                                                                        </div>
                                                                        {
                                                                          tmpTeam.managers.map((v) => (
                                                                            <div
                                                                              style={{
                                                                                border: "solid 1px #CCCCCC",
                                                                                borderRadius: "20px",
                                                                                padding: "4px 8px",
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                gap: "4px",
                                                                                userSelect: "none",
                                                                                backgroundColor: "#FFFFFF",
                                                                              }}
                                                                              key={`target_team_manager_id_${v.id}`}
                                                                            >
                                                                              <img
                                                                                alt="profile"
                                                                                src={
                                                                                  v.profile_image ?? `/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`
                                                                                }
                                                                                className="profile_icon"
                                                                                style={{
                                                                                  borderRadius: "50%",
                                                                                  width: "14px",
                                                                                }}
                                                                              />
                                                                              {v.name}
                                                                            </div>
                                                                          ))
                                                                        }
                                                                        {
                                                                          !tmpTeam.managers.length && (
                                                                            <>
                                                                              このチームにマネージャーは設定されていません
                                                                            </>
                                                                          )
                                                                        }
                                                                      </div>
                                                                    )
                                                                  }
                                                                  {
                                                                    openTargetTeamIds.includes(tmpTeam.id)
                                                                    && ["both", "member"].includes(team.scope)
                                                                    && !team.include_nested_teams && (
                                                                      <div
                                                                        style={{
                                                                          position: "relative",
                                                                          border: "solid 1px #CCCCCC",
                                                                          borderRadius: "4px",
                                                                          backgroundColor: "#f4fbff",
                                                                          padding: "12px 8px 8px",
                                                                          display: "flex",
                                                                          alignItems: "center",
                                                                          flexWrap: "wrap",
                                                                          gap: "4px",
                                                                          marginTop: "4px",
                                                                        }}
                                                                      >
                                                                        <div
                                                                          style={{
                                                                            position: "absolute",
                                                                            top: "0",
                                                                            left: "4px",
                                                                            fontSize: "10px",
                                                                            transform: "translateY(-50%)",
                                                                            padding: "2px 8px",
                                                                            backgroundColor: "#FFFFFF",
                                                                            border: "solid 1px #CCCCCC",
                                                                            borderRadius: "14px",
                                                                          }}
                                                                        >
                                                                          このチームのメンバー
                                                                        </div>
                                                                        {
                                                                          tmpTeam.members.map((v) => (
                                                                            <div
                                                                              style={{
                                                                                border: "solid 1px #CCCCCC",
                                                                                borderRadius: "20px",
                                                                                padding: "4px 8px",
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                gap: "4px",
                                                                                userSelect: "none",
                                                                                backgroundColor: "#FFFFFF",
                                                                              }}
                                                                              key={`target_team_member_id_${v.id}`}
                                                                            >
                                                                              <img
                                                                                alt="profile"
                                                                                src={
                                                                                  v.profile_image ?? `/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`
                                                                                }
                                                                                className="profile_icon"
                                                                                style={{
                                                                                  borderRadius: "50%",
                                                                                  width: "14px",
                                                                                }}
                                                                              />
                                                                              {v.name}
                                                                            </div>
                                                                          ))
                                                                        }
                                                                        {
                                                                          !tmpTeam.members.length && (
                                                                            <>
                                                                              このチームにメンバーは設定されていません
                                                                            </>
                                                                          )
                                                                        }
                                                                      </div>
                                                                    )
                                                                  }
                                                                  {
                                                                    openTargetTeamIds.includes(tmpTeam.id)
                                                                    && ["both", "member"].includes(team.scope)
                                                                    && team.include_nested_teams && (
                                                                      <div
                                                                        style={{
                                                                          position: "relative",
                                                                          border: "solid 1px #CCCCCC",
                                                                          borderRadius: "4px",
                                                                          backgroundColor: "#f4fbff",
                                                                          padding: "12px 8px 8px",
                                                                          display: "flex",
                                                                          alignItems: "center",
                                                                          flexWrap: "wrap",
                                                                          gap: "4px",
                                                                          marginTop: "4px",
                                                                        }}
                                                                      >
                                                                        <div
                                                                          style={{
                                                                            position: "absolute",
                                                                            top: "0",
                                                                            left: "4px",
                                                                            fontSize: "10px",
                                                                            transform: "translateY(-50%)",
                                                                            padding: "2px 8px",
                                                                            backgroundColor: "#FFFFFF",
                                                                            border: "solid 1px #CCCCCC",
                                                                            borderRadius: "14px",
                                                                          }}
                                                                        >
                                                                          このチームのメンバー + 配下チームに所属している全てのマネージャー・メンバー
                                                                        </div>
                                                                        {
                                                                          Array.from(new Map([
                                                                            ...getAllMemberList(team.id),
                                                                            ...tmpTeam.members,
                                                                          ].map((v) => [v.id, v])).values()).sort((a, b) => a.id - b.id).map((v) => (
                                                                            <div
                                                                              style={{
                                                                                border: "solid 1px #CCCCCC",
                                                                                borderRadius: "20px",
                                                                                padding: "4px 8px",
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                gap: "4px",
                                                                                userSelect: "none",
                                                                                backgroundColor: "#FFFFFF",
                                                                              }}
                                                                              key={`target_team_member_id_${v.id}`}
                                                                            >
                                                                              <img
                                                                                alt="profile"
                                                                                src={
                                                                                  v.profile_image ?? `/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`
                                                                                }
                                                                                className="profile_icon"
                                                                                style={{
                                                                                  borderRadius: "50%",
                                                                                  width: "14px",
                                                                                }}
                                                                              />
                                                                              {v.name}
                                                                            </div>
                                                                          ))
                                                                        }
                                                                      </div>
                                                                    )
                                                                  }
                                                                </div>
                                                              )
                                                            })
                                                          }
                                                          {
                                                            !!targetMemberIds.length && (
                                                              <div
                                                                style={{
                                                                  position: "relative",
                                                                  border: targetTeams.length ? "solid 1px #CCCCCC" : "none",
                                                                  borderRadius: targetTeams.length ? "4px" : "none",
                                                                  padding: targetTeams.length ? "8px" : "0",
                                                                  marginTop: targetTeams.length ? "16px" : "0",
                                                                  backgroundColor: "#FFFFFF",
                                                                  display: "flex",
                                                                  alignItems: "center",
                                                                  flexWrap: "wrap",
                                                                  gap: "8px",
                                                                }}
                                                              >
                                                                {
                                                                  !!targetTeams.length && (
                                                                    <div
                                                                      style={{
                                                                        position: "absolute",
                                                                        top: "0",
                                                                        left: "4px",
                                                                        fontSize: "10px",
                                                                        transform: "translateY(-50%)",
                                                                        padding: "0 4px",
                                                                        backgroundColor: "#FFFFFF",
                                                                      }}
                                                                    >
                                                                      その他のユーザー
                                                                    </div>
                                                                  )
                                                                }
                                                                {
                                                                  targetMemberIds.map((memberId) => (
                                                                    <div
                                                                      style={{
                                                                        border: "solid 1px #CCCCCC",
                                                                        borderRadius: "20px",
                                                                        padding: "4px 8px",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: "4px",
                                                                        userSelect: "none",
                                                                      }}
                                                                      key={`target_member_id_${memberId}`}
                                                                    >
                                                                      <img
                                                                        alt="profile"
                                                                        src={
                                                                          memberList.find((v) => v.id === memberId)?.profile_image
                                                                          ?? `/static/images/profile/${(Number(memberId) || 0) % 12}.jpeg`
                                                                        }
                                                                        className="profile_icon"
                                                                        style={{
                                                                          borderRadius: "50%",
                                                                          width: "14px",
                                                                        }}
                                                                      />
                                                                      {memberList.find((v) => v.id === memberId)?.name}
                                                                      <span
                                                                        style={{
                                                                          marginLeft: "4px",
                                                                          cursor: "pointer",
                                                                        }}
                                                                        onClick={() => ejectTargetMemberId(memberId)}
                                                                        role="none"
                                                                      >
                                                                        <FontAwesomeIcon icon={['fas', 'times']} />
                                                                      </span>
                                                                    </div>
                                                                  ))
                                                                }
                                                              </div>
                                                            )
                                                          }
                                                        </div>
                                                      )
                                                    }
                                                    {
                                                      !selectAllTarget && (
                                                        <div
                                                          style={{
                                                            marginTop: "4px",
                                                          }}
                                                        >
                                                          <AddTeamAndMemberModule
                                                            selectMemberIdList={targetMemberIds}
                                                            selectTeamList={targetTeams}
                                                            callback={(result) => selectTarget(result)}
                                                            memberList={
                                                              memberList.map((v) => ({
                                                                id: v.id, name: v.name, kana: v.kana, profile_image: v.profile_image, active: true,
                                                              }))
                                                            }
                                                            teamList={teamList}
                                                            defaultSelectScope="both"
                                                          />
                                                        </div>
                                                      )
                                                    }
                                                  </div>
                                                </div>
                                              )
                                            }
                                            <div
                                              style={{
                                                borderRadius: "4px",
                                                backgroundColor: "#ecf0f4",
                                                padding: "8px",
                                                display: "flex",
                                                flexFlow: "column",
                                                gap: "8px",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "8px",
                                                  flexWrap: "wrap",
                                                }}
                                              >
                                                {
                                                  PERSONAL_OPERATION_LIST.map((v) => (
                                                    <>
                                                      {buildItem(v)}
                                                    </>
                                                  ))
                                                }
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        position: "relative",
                                        display: "flex",
                                        flexFlow: "column",
                                        gap: "16px",
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "4px",
                                        padding: "24px 16px 16px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: "-7px",
                                          left: "12px",
                                          fontSize: "14px",
                                          fontWeight: "bold",
                                          backgroundColor: "#FFFFFF",
                                          padding: "0 4px",
                                        }}
                                      >
                                        権限
                                      </div>
                                      <div>
                                        <div
                                          style={{
                                            width: "100%",
                                            display: "flex",
                                            flexFlow: "column",
                                            gap: "4px",
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontSize: "14px",
                                              fontWeight: "bold",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "8px",
                                              cursor: "pointer",
                                            }}
                                            onClick={() => multiChangeRoleStatus("common_task")}
                                            role="none"
                                          >
                                            <div
                                              style={{
                                                fontSize: "18px",
                                                color: buildCheckColor(checkCommonTaskList),
                                              }}
                                            >
                                              <FontAwesomeIcon
                                                icon={[
                                                  roleList.find((v) => v.id === selectRole?.id)?.editable ? 'far' : 'fas',
                                                  checkCommonTaskList ? 'check-square' : 'square',
                                                ]}
                                              />
                                            </div>
                                            工数管理設定
                                          </div>
                                          <div
                                            style={{
                                              borderRadius: "4px",
                                              backgroundColor: "#F5F0F0",
                                              padding: "8px",
                                              display: "flex",
                                              flexFlow: "column",
                                              gap: "8px",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                flexWrap: "wrap",
                                              }}
                                            >
                                              {
                                                COMMON_TASK_LIST.map((v) => (
                                                  <>
                                                    {buildItem(v)}
                                                  </>
                                                ))
                                              }
                                              {
                                                login.feature_config?.enable_project_member_setting && (
                                                  <>
                                                    {buildItem({
                                                      key: "task_project_member_setting",
                                                      title: "プロジェクト業務仕分け対象設定権限",
                                                      description: "プロジェクト設定で業務仕分け対象のみ設定できます。",
                                                      url: "",
                                                    })}
                                                  </>
                                                )
                                              }
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                )
                              }
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          style={{
                            width: "100%",
                            height: "calc(100vh - 200px)",
                            display: "flex",
                            flexFlow: "column",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            style={{
                              textAlign: "center",
                              fontSize: "40px",
                              fontWeight: "bold",
                              color: "#4472ba",
                              marginBottom: "40px",
                            }}
                          >
                            権限ロール設定が制限されています
                          </div>
                          <div
                            style={{
                              fontSize: "18px",
                              color: "#666666",
                              display: "flex",
                              flexFlow: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: "16px",
                            }}
                          >
                            <div>
                              Qaseeをご利用いただき、誠にありがとうございます。
                            </div>
                            <div>
                              トライアル期間が終了したため、権限ロール設定が制限されています。
                            </div>
                            <img src="/static/images/image_1.png" alt="" style={{ width: "200px" }} />
                          </div>
                        </div>
                      </>
                    )
                  }
                </>
              )
            }
          </div>
        </div>
      </section>
      <Modal
        show={isDisplayDeleteRoleModal}
        wrapperClass="w500"
        closeCallback={closeDeleteRoleModal}
        closeable
      >
        <>
          <div
            style={{
              textAlign: "center",
              fontSize: "20px",
              marginBottom: "16px",
            }}
          >
            ロールの削除
          </div>
          <div
            style={{
              display: "flex",
              flexFlow: "column",
              gap: "8px",
              padding: "16px",
            }}
          >
            <div>
              {`ロール「${roleList.find((v) => v.id === deleteRoleId)?.name}」を削除します。`}
            </div>
            <div>
              ロールを削除すると、権限対象ユーザーは自動的に「一般ユーザー権限」に変更されます。
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "8px",
              gap: "8px",
            }}
          >
            <button
              type="button"
              className="button cancel"
              onClick={closeDeleteRoleModal}
              style={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #656E73",
                color: "#656E73",
                width: "200px",
                textAlign: "center",
                borderRadius: "4px",
              }}
            >
              キャンセル
            </button>
            <button
              type="button"
              className="button delete"
              onClick={() => (
                roleList.find((v) => v.id === deleteRoleId)?.attached_member_ids.includes(login.current_member_id || 0)
                  ? openConfirmRoleModal(() => {
                    deleteRole(deleteRoleId, true)
                  })
                  : deleteRole(deleteRoleId)
              )}
              style={{
                backgroundColor: "#C61616",
                border: "1px solid #C61616",
                color: "#FFFFFF",
                width: "200px",
                textAlign: "center",
                borderRadius: "4px",
              }}
            >
              削除する
            </button>
          </div>
        </>
      </Modal>
      <Modal
        show={confirmModalState.display}
        wrapperClass="w500"
        closeCallback={() => setConfirmModalState((cur) => ({ ...cur, display: false }))}
        closeable
      >
        <>
          <div
            style={{
              textAlign: "center",
              fontSize: "20px",
              marginBottom: "16px",
            }}
          >
            ユーザーの確認
          </div>
          <div
            style={{
              display: "flex",
              flexFlow: "column",
              gap: "8px",
              padding: "16px",
            }}
          >
            <div>
              他の権限ロールに設定されているユーザーが選択されています。
            </div>
            <div>
              選択したユーザーを現在の権限ロールに変更します。
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "8px",
              gap: "8px",
            }}
          >
            <button
              type="button"
              className="button cancel"
              onClick={() => setConfirmModalState((cur) => ({ ...cur, display: false }))}
              style={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #656E73",
                color: "#656E73",
                width: "200px",
                textAlign: "center",
                borderRadius: "4px",
              }}
            >
              キャンセル
            </button>
            <button
              type="button"
              className="button"
              onClick={() => confirmModalState.callback()}
              style={{
                backgroundColor: "#1630c6",
                border: "1px solid #1630c6",
                color: "#FFFFFF",
                width: "200px",
                textAlign: "center",
                borderRadius: "4px",
              }}
            >
              確認しました
            </button>
          </div>
        </>
      </Modal>
      <Modal
        show={confirmRoleModalState.display}
        wrapperClass="w500"
        closeCallback={() => setConfirmRoleModalState((cur) => ({ ...cur, display: false, check: false }))}
        closeable
      >
        <>
          <div
            style={{
              textAlign: "center",
              fontSize: "20px",
              marginBottom: "16px",
            }}
          >
            権限ロール設定権限の確認
          </div>
          <div
            style={{
              display: "flex",
              flexFlow: "column",
              gap: "8px",
              padding: "16px",
            }}
          >
            <div>
              自身の権限ロールを解除する、または、自身が含まれている権限ロール設定権限をOFFにすると、権限ロール設定ができなくなります。
            </div>
            <div>
              本当に設定を反映してよろしいですか？
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                border: "solid 1px #666666",
                borderRadius: "4px",
                padding: "8px",
                color: confirmRoleModalState.check ? "#008800" : "#999999",
                userSelect: "none",
                cursor: "pointer",
              }}
              onClick={() => setConfirmRoleModalState((cur) => ({ ...cur, check: !cur.check }))}
              role="none"
            >
              <FontAwesomeIcon
                icon={[
                  'far',
                  confirmRoleModalState.check ? 'check-square' : 'square',
                ]}
              />
              確認しました
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "8px",
              gap: "8px",
            }}
          >
            <button
              type="button"
              className="button cancel"
              onClick={() => setConfirmRoleModalState((cur) => ({ ...cur, display: false, check: false }))}
              style={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #656E73",
                color: "#656E73",
                width: "200px",
                textAlign: "center",
                borderRadius: "4px",
              }}
            >
              キャンセル
            </button>
            <button
              type="button"
              className="button"
              onClick={() => confirmRoleModalState.callback()}
              style={{
                backgroundColor: confirmRoleModalState.check ? "#1630c6" : "#666666",
                border: `1px solid ${confirmRoleModalState.check ? "#1630c6" : "#666666"}`,
                color: "#FFFFFF",
                width: "200px",
                textAlign: "center",
                borderRadius: "4px",
                cursor: confirmRoleModalState.check ? "pointer" : "not-allowed",
              }}
              disabled={!confirmRoleModalState.check}
            >
              設定する
            </button>
          </div>
        </>
      </Modal>
    </section>
  )
}

export default Index
